import {
  LocationTreeWithRoot,
  Location,
  NodeParents,
  NodeIndex,
  BranchLocation,
} from '../types';
import normalizeLocationId from './normalizeLocationId';
import { isBranch } from './validations';

function processChildren(
  children: Location[],
  fn: (item: Location) => Location,
  targetId?: string
): Location[] {
  return children.map((item) => {
    const newNode = fn({ ...item });

    if (
      normalizeLocationId(targetId) &&
      normalizeLocationId(newNode.id) === normalizeLocationId(targetId)
    ) {
      return newNode;
    }

    if (isBranch(newNode) && newNode.children.length > 0) {
      const distinctChildren = [...newNode.children];
      if (distinctChildren.length > 0) {
        newNode.children = processChildren(
          distinctChildren,
          fn,
          normalizeLocationId(targetId)
        );
      }
    }

    return newNode;
  });
}

export function recursiveTreeMap(
  tree: LocationTreeWithRoot,
  fn: (item: Location) => Location,
  targetId?: string
): LocationTreeWithRoot {
  if (tree.length === 0) return tree;

  const [root] = tree;

  const processedRoot = { ...root };

  if (root.children.length > 0) {
    const distinctChildren = [...root.children];
    if (distinctChildren.length > 0) {
      processedRoot.children = processChildren(
        distinctChildren,
        fn,
        normalizeLocationId(targetId)
      );
    }
  }

  return [processedRoot];
}

export function editRecursive(
  tree: LocationTreeWithRoot,
  newNode: Location
): LocationTreeWithRoot {
  const newTree = recursiveTreeMap(
    tree,
    (node) => {
      if (normalizeLocationId(node.id) === normalizeLocationId(newNode.id))
        return { ...node, ...newNode };
      return node;
    },
    normalizeLocationId(newNode.id)
  );

  return newTree;
}

export function indexNodeParents(tree: LocationTreeWithRoot): NodeParents {
  const nodeParents: NodeParents = {};
  const [root] = tree;

  function initializeNodeParents(children: Location[], parent: BranchLocation) {
    children.forEach((child) => {
      nodeParents[normalizeLocationId(child.id)] = parent;

      if (isBranch(child) && child.children.length > 0) {
        const distinctChildren = [...child.children];
        if (distinctChildren.length > 0) {
          initializeNodeParents(distinctChildren, child);
        }
      }
    });
  }

  initializeNodeParents(root.children, root);

  return nodeParents;
}

export function createNodeIndex(tree: LocationTreeWithRoot): NodeIndex {
  const nodeIndex: NodeIndex = {};
  const [root] = tree;

  function indexNodes(children: Location[], parent: BranchLocation) {
    children.forEach((child, i) => {
      nodeIndex[normalizeLocationId(child.id)] = {
        node: child,
        parent,
        siblingIndex: i,
      };

      if (isBranch(child) && child.children.length > 0) {
        const distinctChildren = [...child.children];
        if (distinctChildren.length > 0) {
          indexNodes(distinctChildren, child);
        }
      }
    });
  }

  indexNodes(root.children, root);

  return nodeIndex;
}
